import { useState } from 'react'
import z from 'zod'
import { DEFAULT_LAST_DAYS } from '@/utils/date/last-days-range'

const SETTINGS_KEY = 'tactrl-settings'

const SidebarGroupsCollapsedShape = z.object({
  general: z.boolean(),
  workTimeAndSalary: z.boolean(),
  documentation: z.boolean(),
  fleet: z.boolean(),
  analytics: z.boolean(),
  forms: z.boolean(),
  information: z.boolean(),
  routePlanner: z.boolean(),
  exportAndSettings: z.boolean(),
})

const SettingsShape = z.object({
  absenceCardCollapsed: z.object({
    files: z.boolean(),
    comments: z.boolean(),
    logs: z.boolean(),
  }),
  infringementCardCollapsed: z.object({
    statistics: z.boolean(),
    attachments: z.boolean(),
    logs: z.boolean(),
  }),
  sidebarGroupsCollapsed: SidebarGroupsCollapsedShape,
  datePickerLastDays: z.number(),
})

type Settings = z.infer<typeof SettingsShape>

/** Identifies a collapsible group in the sidebar menu. */
export type SidebarGroupId = keyof z.infer<typeof SidebarGroupsCollapsedShape>

type SettingsObject = Record<string, unknown>

type SettingPath<T> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? `${K}` | `${K}.${SettingPath<T[K]>}`
        : `${K}`
    }[keyof T & string]
  : never

type SettingPathValue<
  T,
  P extends string,
> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? SettingPathValue<T[K], Rest>
    : never
  : P extends keyof T
  ? T[P]
  : never

type SettingUpdater<TValue> = (current: TValue) => TValue
type SettingInput<TValue> = TValue | SettingUpdater<TValue>

export type LocalStorageSettingPath = SettingPath<Settings>

function isPlainObject(value: unknown): value is SettingsObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function mergeWithDefaults<T>(defaults: T, current: unknown): T {
  if (!isPlainObject(defaults)) return defaults
  if (!isPlainObject(current)) return defaults

  const result: SettingsObject = {}

  for (const [key, defaultValue] of Object.entries(defaults)) {
    const currentValue = current[key]

    if (isPlainObject(defaultValue)) {
      result[key] = mergeWithDefaults(defaultValue, currentValue)
      continue
    }

    result[key] =
      typeof currentValue === typeof defaultValue ? currentValue : defaultValue
  }

  return result as T
}

function setByPath<T extends SettingsObject, P extends SettingPath<T>>(
  object: T,
  path: P,
  value: SettingPathValue<T, P>
): T {
  const keys = path.split('.')
  const next: SettingsObject = { ...object }
  let cursor: SettingsObject = next

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]!
    const child = cursor[key]

    cursor[key] = isPlainObject(child) ? { ...child } : {}
    cursor = cursor[key] as SettingsObject
  }

  cursor[keys[keys.length - 1]!] = value as unknown

  return next as T
}

function getByPath<T extends SettingsObject, P extends SettingPath<T>>(
  object: T,
  path: P
): SettingPathValue<T, P> {
  const keys = path.split('.')
  let cursor: unknown = object

  for (const key of keys) {
    if (!isPlainObject(cursor)) break
    cursor = cursor[key]
  }

  return cursor as SettingPathValue<T, P>
}

function resolveSettingInput<TValue>(
  current: TValue,
  next: SettingInput<TValue>
): TValue {
  return typeof next === 'function'
    ? (next as SettingUpdater<TValue>)(current)
    : next
}

function useLocalStorageSettings() {
  if (typeof window === 'undefined')
    throw new Error('useLocalStorageSettings can only be used in the browser')

  function initSettings(): Settings {
    return {
      absenceCardCollapsed: {
        files: false,
        comments: false,
        logs: false,
      },
      infringementCardCollapsed: {
        statistics: true,
        attachments: false,
        logs: false,
      },
      sidebarGroupsCollapsed: {
        general: false,
        workTimeAndSalary: false,
        documentation: false,
        fleet: false,
        analytics: false,
        forms: false,
        information: false,
        routePlanner: false,
        exportAndSettings: false,
      },
      datePickerLastDays: DEFAULT_LAST_DAYS,
    }
  }

  function setSettings(newSettings: Settings) {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings))
  }

  function getSettings() {
    const defaults = initSettings()
    const item = window.localStorage.getItem(SETTINGS_KEY)

    if (!item) {
      setSettings(defaults)
      return defaults
    }

    try {
      const parsed = JSON.parse(item)
      const merged = mergeWithDefaults(defaults, parsed)
      const validated = SettingsShape.parse(merged)

      // Persist merged settings so new keys are written without losing existing values.
      if (JSON.stringify(validated) !== item) {
        setSettings(validated)
      }

      return validated
    } catch (error) {
      console.error(
        'Failed to parse settings from localStorage, resetting invalid payload.',
        error
      )
      setSettings(defaults)
      return defaults
    }
  }

  function getSetting<TPath extends SettingPath<Settings>>(
    path: TPath
  ): SettingPathValue<Settings, TPath> {
    const settings = getSettings()
    return getByPath(settings, path)
  }

  function updateSetting<TPath extends SettingPath<Settings>>(
    path: TPath,
    valueOrUpdater: SettingInput<SettingPathValue<Settings, TPath>>
  ) {
    const settings = getSettings()
    const currentValue = getByPath(settings, path)
    const resolvedValue = resolveSettingInput(currentValue, valueOrUpdater)
    const nextSettings = setByPath(settings, path, resolvedValue)
    setSettings(nextSettings)
    return nextSettings
  }

  return {
    getSetting,
    updateSetting,
  }
}

export function useLocalStorageSetting<TPath extends SettingPath<Settings>>(
  path: TPath
): [
  SettingPathValue<Settings, TPath>,
  (valueOrUpdater: SettingInput<SettingPathValue<Settings, TPath>>) => void,
] {
  const { getSetting, updateSetting } = useLocalStorageSettings()
  const [setting, setSetting] = useState(() => getSetting(path))

  function updateAndPersistSetting(
    valueOrUpdater: SettingInput<SettingPathValue<Settings, TPath>>
  ) {
    const nextSettings = updateSetting(path, valueOrUpdater)
    setSetting(getByPath(nextSettings, path))
  }

  return [setting, updateAndPersistSetting]
}
